// =============================================================================
// LP Express section model — single source of truth for the page's ordered,
// typed sections.
//
// Historically an LP Express page was a nested `generatedContent` object keyed
// by fixed section names (hero, advisory, stations, ...) and rendered in a
// hardcoded order. This module migrates that to a discrete, ordered, typed
// sections model: `sections: Array<{ id, type, fields }>`. The renderer maps
// over the ordered array and dispatches each entry to its existing rich
// per-section component, so the visual output is unchanged — but the order and
// membership of sections now live in data, which a future editor can reorder
// or trim.
//
// Granularity: a "section" here is a top-level renderable BLOCK (the unit a
// user would reorder), reusing the existing rich per-section components. The
// underlying fine-grained content types (hero, leadin, advisory, stations,
// showcase, productContext, villain, originCopy, and the image-slot groupings)
// are enumerated per block in `contentTypes` / `contentKeys` below — several of
// them (villain, productContext, quotes) are deliberately shared across blocks,
// which is why those rich composite components are reused.
//
// The former single "remaining" composite (the bottom long-form page) has been
// decomposed into individually dispatchable, reorderable sub-section blocks
// (timeline, membership, proof, alternatives, philosophy, differentiator, faq,
// reviews, finalCta, footer). They all still render through the
// `RemainingPageSections` component via its `section` prop, with the
// interstitial dividers (SpringSaleBanner / Marquee) traveling with their
// anchor block so the default order stays visually byte-identical. Legacy
// persisted/published pages that still reference "remaining" are expanded in
// place at read time (see LEGACY_COMPOSITE_EXPANSIONS + getLpSections).
// =============================================================================

import { GOOGLE_FONT_CATALOG, type GoogleFontCategory } from "./google-fonts";
import { LP_SMB_HOME_BLOCK_KEYS } from "./lp-express-smb-home";
import type { BrandFont } from "./schema";
import {
  brandFontCssStack,
  BRAND_FONT_SANS_FALLBACK,
  BRAND_FONT_SERIF_FALLBACK,
  isUsableBrandFont,
} from "./brand-fonts";

// Canonical render order of the LONG-FORM template's top-level section blocks.
// Mirrors the legacy hardcoded dispatch order in `lp-express-renderer.tsx`
// exactly. This is the default section set for every page whose templateType
// is not listed in LP_TEMPLATE_SECTION_TYPES below.
export const LP_LONG_FORM_SECTION_TYPES = [
  "announcement",
  "stickyNav",
  "hero",
  "productShowcase",
  "advisory",
  "problemLoop",
  "belief",
  "farmstand",
  // The long-form page (formerly the single "remaining" composite) is now
  // decomposed into individually dispatchable, reorderable sub-section blocks.
  // Their canonical order below reproduces the legacy composite's fixed
  // internal order exactly. Interstitial dividers (SpringSaleBanner / Marquee)
  // travel with their anchor block (see RemainingPageSections' `section` prop).
  "timeline",
  "membership",
  "proof",
  "alternatives",
  "philosophy",
  "differentiator",
  "faq",
  "reviews",
  "finalCta",
  "footer",
] as const;

// Canonical render order of the Product Hero (PDP) template's section blocks
// (templateType === "product-hero"). Each block's copy lives under its own
// same-named generatedContent key (split from the single product-hero workflow
// output by the server — see shared/lp-express-product-hero.ts). All blocks
// dispatch through the `LpExpressProductHero` component via its `section`
// prop, mirroring how the long-form sub-sections dispatch through
// `RemainingPageSections`.
export const LP_PRODUCT_HERO_SECTION_TYPES = [
  "phBuyBox",
  "phTrustBar",
  "phBenefits",
  "phHowItWorks",
  "phReviews",
  "phGuarantee",
  "phFaq",
  "phFinalCta",
  "phDisclaimer",
  "phStickyCta",
] as const;

// Canonical render order of the Problem / Solution (PAS / unique-mechanism)
// template's section blocks (templateType === "problem-solution"). Each
// block's copy lives under its own same-named generatedContent key (split from
// the single problem-solution workflow output by the server — see
// shared/lp-express-problem-solution.ts). All blocks dispatch through the
// `LpExpressProblemSolution` component via its `section` prop, mirroring the
// product-hero pattern.
export const LP_PROBLEM_SOLUTION_SECTION_TYPES = [
  "psHero",
  "psProblem",
  "psWhyFail",
  "psMechanism",
  "psSolution",
  "psProof",
  "psOffer",
  "psGuarantee",
  "psFaq",
  "psDisclaimer",
  "psStickyCta",
] as const;

// Canonical render order of the Offer Stack (value-stack close) template's
// section blocks (templateType === "offer-stack"). Each block's copy lives
// under its own same-named generatedContent key (split from the single
// offer-stack workflow output by the server — see
// shared/lp-express-offer-stack.ts). All blocks dispatch through the
// `LpExpressOfferStack` component via its `section` prop, mirroring the
// product-hero / problem-solution pattern.
export const LP_OFFER_STACK_SECTION_TYPES = [
  "osHero",
  "osIntro",
  "osStack",
  "osBonuses",
  "osScarcity",
  "osReviews",
  "osGuarantee",
  "osFaq",
  "osDisclaimer",
  "osStickyCta",
] as const;

// The union of every known section type across templates. Server-side
// validation (section-order route) accepts any member; which subset a given
// page actually derives is template-scoped (see lpTemplateSectionTypes /
// deriveLpSections).
export const LP_SECTION_TYPES = [
  ...LP_SMB_HOME_BLOCK_KEYS,
  ...LP_LONG_FORM_SECTION_TYPES,
  ...LP_PRODUCT_HERO_SECTION_TYPES,
  ...LP_PROBLEM_SOLUTION_SECTION_TYPES,
  ...LP_OFFER_STACK_SECTION_TYPES,
] as const;

export type LpSectionType = (typeof LP_SECTION_TYPES)[number];

export interface LpSectionDef {
  type: LpSectionType;
  label: string;
  order: number;
  // True when the block renders regardless of generatedContent (driven by
  // brand/sale, not by AI output).
  alwaysPresent: boolean;
  // The renderer component that renders this block.
  component: string;
  // The fine-grained content section types this block carries (the task's
  // enumerated section types). Documentation / future-editor metadata.
  contentTypes: string[];
  // The `generatedContent` keys this block reads. Used by the field extractor.
  contentKeys: string[];
}

// The registry: every top-level section block, in canonical order, with the
// fine-grained content types and generatedContent keys it carries.
export const LP_SECTION_REGISTRY: Record<LpSectionType, LpSectionDef> = {
  ...Object.fromEntries(LP_SMB_HOME_BLOCK_KEYS.map((type, order) => [type, {
    type, label: type.slice(2).replace(/([a-z])([A-Z])/g, "$1 $2"), order,
    alwaysPresent: false, component: "LpExpressStudioTemplate",
    contentTypes: [type], contentKeys: [type],
  }])) as Record<typeof LP_SMB_HOME_BLOCK_KEYS[number], LpSectionDef>,
  announcement: {
    type: "announcement",
    label: "Announcement Bar",
    order: 0,
    alwaysPresent: true,
    component: "AnnouncementBar",
    contentTypes: [],
    contentKeys: [],
  },
  stickyNav: {
    type: "stickyNav",
    label: "Sticky Nav",
    order: 1,
    alwaysPresent: true,
    component: "StickyNav",
    contentTypes: [],
    contentKeys: [],
  },
  hero: {
    type: "hero",
    label: "Hero",
    order: 2,
    alwaysPresent: false,
    component: "HeroSection",
    contentTypes: ["hero", "leadin", "heroImages"],
    contentKeys: ["hero", "leadin", "heroImages", "heroImagesLoading", "heroImageStatuses"],
  },
  productShowcase: {
    type: "productShowcase",
    label: "Product Showcase",
    order: 3,
    alwaysPresent: false,
    component: "ProductShowcaseSection",
    contentTypes: ["showcase", "productContext"],
    contentKeys: ["showcase", "productContext"],
  },
  advisory: {
    type: "advisory",
    label: "Public Advisory",
    order: 4,
    alwaysPresent: false,
    component: "PsaWarningSection",
    contentTypes: ["advisory"],
    contentKeys: ["advisory"],
  },
  problemLoop: {
    type: "problemLoop",
    label: "Problem Loop",
    order: 5,
    alwaysPresent: false,
    component: "ProblemLoopSection",
    contentTypes: ["stations", "villain"],
    contentKeys: ["stations", "villain", "quotes"],
  },
  belief: {
    type: "belief",
    label: "Belief",
    order: 6,
    alwaysPresent: false,
    component: "BeliefSection",
    contentTypes: ["villain", "productContext"],
    contentKeys: ["villain", "productContext", "quotes"],
  },
  farmstand: {
    type: "farmstand",
    label: "Origin",
    order: 7,
    alwaysPresent: false,
    component: "FarmstandSection",
    contentTypes: ["originCopy", "originImages"],
    contentKeys: ["originCopy", "originImages", "quotes"],
  },
  // ── Long-form page sub-sections (formerly the "remaining" composite). Each
  //    is now a first-class, reorderable block. Order 8..17 reproduces the
  //    legacy composite's fixed internal order. They all dispatch through the
  //    `RemainingPageSections` component via its `section` prop.
  timeline: {
    type: "timeline",
    label: "90-Day Timeline",
    order: 8,
    alwaysPresent: false,
    component: "RemainingPageSections",
    contentTypes: ["timeline"],
    contentKeys: ["timeline"],
  },
  membership: {
    type: "membership",
    label: "Membership / Freshness",
    order: 9,
    alwaysPresent: false,
    component: "RemainingPageSections",
    contentTypes: ["membership"],
    contentKeys: ["membership", "quotes"],
  },
  proof: {
    type: "proof",
    label: "Proof",
    order: 10,
    alwaysPresent: false,
    component: "RemainingPageSections",
    contentTypes: ["proof", "proofImages"],
    contentKeys: ["proof", "proofImages"],
  },
  alternatives: {
    type: "alternatives",
    label: "Alternatives",
    order: 11,
    alwaysPresent: false,
    component: "RemainingPageSections",
    contentTypes: ["alternatives", "alternativesImages"],
    contentKeys: ["alternatives", "alternativesImages", "quotes"],
  },
  philosophy: {
    type: "philosophy",
    label: "Philosophy",
    order: 12,
    alwaysPresent: false,
    component: "RemainingPageSections",
    contentTypes: ["philosophy", "philosophyImages"],
    contentKeys: ["philosophy", "philosophyImages"],
  },
  differentiator: {
    type: "differentiator",
    label: "What Makes This Different",
    order: 13,
    alwaysPresent: false,
    component: "RemainingPageSections",
    contentTypes: ["differentiator"],
    contentKeys: ["differentiator", "proofImages"],
  },
  faq: {
    type: "faq",
    label: "FAQ",
    order: 14,
    alwaysPresent: false,
    component: "RemainingPageSections",
    contentTypes: ["faq"],
    contentKeys: ["faq"],
  },
  reviews: {
    type: "reviews",
    label: "Reviews",
    order: 15,
    alwaysPresent: false,
    component: "RemainingPageSections",
    contentTypes: ["reviews"],
    contentKeys: ["reviews"],
  },
  finalCta: {
    type: "finalCta",
    label: "Final CTA",
    order: 16,
    alwaysPresent: false,
    component: "RemainingPageSections",
    contentTypes: ["finalCta"],
    contentKeys: ["finalCta", "quotes"],
  },
  footer: {
    type: "footer",
    label: "Footer",
    order: 17,
    alwaysPresent: true,
    component: "RemainingPageSections",
    contentTypes: [],
    contentKeys: ["footer"],
  },
  // ── Product Hero (PDP) template sections (templateType === "product-hero").
  //    Each block carries exactly one same-named generatedContent copy key;
  //    all dispatch through the `LpExpressProductHero` component via its
  //    `section` prop. Product imagery (gallery main + thumbnails) is the
  //    brand's REAL product photos surfaced through mediaOverrides — never
  //    AI-generated — so none of these appear in LP_IMAGE_SECTION_TYPES.
  phBuyBox: {
    type: "phBuyBox",
    label: "Hero / Buy Box",
    order: 18,
    alwaysPresent: false,
    component: "LpExpressProductHero",
    contentTypes: ["phBuyBox"],
    contentKeys: ["phBuyBox"],
  },
  phTrustBar: {
    type: "phTrustBar",
    label: "Press / Trust Bar",
    order: 19,
    alwaysPresent: false,
    component: "LpExpressProductHero",
    contentTypes: ["phTrustBar"],
    contentKeys: ["phTrustBar"],
  },
  phBenefits: {
    type: "phBenefits",
    label: "Benefit Grid",
    order: 20,
    alwaysPresent: false,
    component: "LpExpressProductHero",
    contentTypes: ["phBenefits"],
    contentKeys: ["phBenefits"],
  },
  phHowItWorks: {
    type: "phHowItWorks",
    label: "How It Works",
    order: 21,
    alwaysPresent: false,
    component: "LpExpressProductHero",
    contentTypes: ["phHowItWorks"],
    contentKeys: ["phHowItWorks"],
  },
  phReviews: {
    type: "phReviews",
    label: "Reviews Wall",
    order: 22,
    alwaysPresent: false,
    component: "LpExpressProductHero",
    contentTypes: ["phReviews"],
    contentKeys: ["phReviews"],
  },
  phGuarantee: {
    type: "phGuarantee",
    label: "Guarantee",
    order: 23,
    alwaysPresent: false,
    component: "LpExpressProductHero",
    contentTypes: ["phGuarantee"],
    contentKeys: ["phGuarantee"],
  },
  phFaq: {
    type: "phFaq",
    label: "FAQ",
    order: 24,
    alwaysPresent: false,
    component: "LpExpressProductHero",
    contentTypes: ["phFaq"],
    contentKeys: ["phFaq"],
  },
  phFinalCta: {
    type: "phFinalCta",
    label: "Closing CTA",
    order: 25,
    alwaysPresent: false,
    component: "LpExpressProductHero",
    contentTypes: ["phFinalCta"],
    contentKeys: ["phFinalCta"],
  },
  phDisclaimer: {
    type: "phDisclaimer",
    label: "Disclaimer",
    order: 26,
    alwaysPresent: false,
    component: "LpExpressProductHero",
    contentTypes: ["phDisclaimer"],
    contentKeys: ["phDisclaimer"],
  },
  phStickyCta: {
    type: "phStickyCta",
    label: "Sticky CTA Bar",
    order: 27,
    alwaysPresent: false,
    component: "LpExpressProductHero",
    contentTypes: ["phStickyCta"],
    contentKeys: ["phStickyCta"],
  },
  // ── Problem / Solution (PAS) template sections
  //    (templateType === "problem-solution"). Each block carries exactly one
  //    same-named generatedContent copy key; all dispatch through the
  //    `LpExpressProblemSolution` component via its `section` prop. The
  //    mechanism section's AI diagram rides the problemSolutionImages slot
  //    array (single-slot image pipeline), and the solution section's product
  //    image is a REAL product photo via mediaOverrides — so none of these
  //    appear in LP_IMAGE_SECTION_TYPES.
  psHero: {
    type: "psHero",
    label: "Hero",
    order: 28,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psHero"],
    contentKeys: ["psHero"],
  },
  psProblem: {
    type: "psProblem",
    label: "Problem",
    order: 29,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psProblem"],
    contentKeys: ["psProblem"],
  },
  psWhyFail: {
    type: "psWhyFail",
    label: "Why Fixes Fail",
    order: 30,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psWhyFail"],
    contentKeys: ["psWhyFail"],
  },
  psMechanism: {
    type: "psMechanism",
    label: "The Mechanism",
    order: 31,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psMechanism"],
    contentKeys: ["psMechanism", "problemSolutionImages"],
  },
  psSolution: {
    type: "psSolution",
    label: "Solution Reveal",
    order: 32,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psSolution"],
    contentKeys: ["psSolution"],
  },
  psProof: {
    type: "psProof",
    label: "Proof",
    order: 33,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psProof"],
    contentKeys: ["psProof"],
  },
  psOffer: {
    type: "psOffer",
    label: "Offer Box",
    order: 34,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psOffer"],
    contentKeys: ["psOffer"],
  },
  psGuarantee: {
    type: "psGuarantee",
    label: "Guarantee",
    order: 35,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psGuarantee"],
    contentKeys: ["psGuarantee"],
  },
  psFaq: {
    type: "psFaq",
    label: "FAQ",
    order: 36,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psFaq"],
    contentKeys: ["psFaq"],
  },
  psDisclaimer: {
    type: "psDisclaimer",
    label: "Disclaimer",
    order: 37,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psDisclaimer"],
    contentKeys: ["psDisclaimer"],
  },
  psStickyCta: {
    type: "psStickyCta",
    label: "Sticky CTA Bar",
    order: 38,
    alwaysPresent: false,
    component: "LpExpressProblemSolution",
    contentTypes: ["psStickyCta"],
    contentKeys: ["psStickyCta"],
  },
  // ── Offer Stack (value-stack close) template sections
  //    (templateType === "offer-stack"). Each block carries exactly one
  //    same-named generatedContent copy key; all dispatch through the
  //    `LpExpressOfferStack` component via its `section` prop. There are NO
  //    AI image slots — the intro section's product image is a REAL product
  //    photo via mediaOverrides — so none of these appear in
  //    LP_IMAGE_SECTION_TYPES.
  osHero: {
    type: "osHero",
    label: "Hero",
    order: 39,
    alwaysPresent: false,
    component: "LpExpressOfferStack",
    contentTypes: ["osHero"],
    contentKeys: ["osHero"],
  },
  osIntro: {
    type: "osIntro",
    label: "Product Intro",
    order: 40,
    alwaysPresent: false,
    component: "LpExpressOfferStack",
    contentTypes: ["osIntro"],
    contentKeys: ["osIntro", "offerStackImages"],
  },
  osStack: {
    type: "osStack",
    label: "Value Stack",
    order: 41,
    alwaysPresent: false,
    component: "LpExpressOfferStack",
    contentTypes: ["osStack"],
    contentKeys: ["osStack"],
  },
  osBonuses: {
    type: "osBonuses",
    label: "Fast-Action Bonuses",
    order: 42,
    alwaysPresent: false,
    component: "LpExpressOfferStack",
    contentTypes: ["osBonuses"],
    contentKeys: ["osBonuses"],
  },
  osScarcity: {
    type: "osScarcity",
    label: "Scarcity",
    order: 43,
    alwaysPresent: false,
    component: "LpExpressOfferStack",
    contentTypes: ["osScarcity"],
    contentKeys: ["osScarcity"],
  },
  osReviews: {
    type: "osReviews",
    label: "Reviews",
    order: 44,
    alwaysPresent: false,
    component: "LpExpressOfferStack",
    contentTypes: ["osReviews"],
    contentKeys: ["osReviews"],
  },
  osGuarantee: {
    type: "osGuarantee",
    label: "Guarantee",
    order: 45,
    alwaysPresent: false,
    component: "LpExpressOfferStack",
    contentTypes: ["osGuarantee"],
    contentKeys: ["osGuarantee"],
  },
  osFaq: {
    type: "osFaq",
    label: "FAQ",
    order: 46,
    alwaysPresent: false,
    component: "LpExpressOfferStack",
    contentTypes: ["osFaq"],
    contentKeys: ["osFaq"],
  },
  osDisclaimer: {
    type: "osDisclaimer",
    label: "Disclaimer",
    order: 47,
    alwaysPresent: false,
    component: "LpExpressOfferStack",
    contentTypes: ["osDisclaimer"],
    contentKeys: ["osDisclaimer"],
  },
  osStickyCta: {
    type: "osStickyCta",
    label: "Sticky CTA Bar",
    order: 48,
    alwaysPresent: false,
    component: "LpExpressOfferStack",
    contentTypes: ["osStickyCta"],
    contentKeys: ["osStickyCta"],
  },
};

// Legacy composite section types and the ordered sub-sections they expand into.
// Older persisted `sections` arrays (and published snapshots) reference the
// single "remaining" block; we expand it in-place into its sub-sections so
// those pages keep rendering the full long-form page in canonical order.
export const LEGACY_COMPOSITE_EXPANSIONS: Record<string, LpSectionType[]> = {
  remaining: [
    "timeline",
    "membership",
    "proof",
    "alternatives",
    "philosophy",
    "differentiator",
    "faq",
    "reviews",
    "finalCta",
    "footer",
  ],
};

// A single ordered, typed section. `fields` is a view of the slice of
// generatedContent this section renders — always re-derived from the live
// content at read time (see `getLpSections`), so per-section merge writes to
// the underlying content keys flow through without keeping a divergent copy.
export interface LpSection {
  id: string;
  type: LpSectionType;
  fields: Record<string, unknown>;
  // Editor visibility. A hidden section stays in the ordered array (so the
  // editor can show + restore it) but is skipped on the published page. Absent /
  // false = visible. Deleting a section instead omits it from the array entirely.
  hidden?: boolean;
}

export function isLpSectionType(v: unknown): v is LpSectionType {
  return typeof v === "string" && (LP_SECTION_TYPES as readonly string[]).includes(v);
}

// Scroll offset (px) applied as scroll-margin-top to every section anchor so the
// sticky nav doesn't cover the section heading when an in-page #anchor link
// jumps to it.
export const LP_SECTION_SCROLL_MARGIN = 96;

// Deterministic, human-stable HTML id for a section's rendered root. Stable
// across reorders (derived purely from the section type) and identical in the
// builder and on the public page. Reviews keeps the bare "reviews" id so the
// pre-existing hero "X reviews" link (href="#reviews") resolves; every other
// section uses an "lp-" prefix to avoid clashing with arbitrary page ids.
export function lpSectionAnchorId(type: LpSectionType): string {
  if (type === "reviews") return "reviews";
  const kebab = type.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
  return `lp-${kebab}`;
}

function asContent(gc: unknown): Record<string, unknown> {
  return gc && typeof gc === "object" ? (gc as Record<string, unknown>) : {};
}

function pick(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    if (k in obj) out[k] = obj[k];
  }
  return out;
}

// Extract the slice of generatedContent that a section block renders.
export function extractSectionFields(
  type: LpSectionType,
  gc: unknown,
): Record<string, unknown> {
  const content = asContent(gc);
  const def = LP_SECTION_REGISTRY[type];
  return pick(content, def?.contentKeys ?? []);
}

// ── Regenerate-with-feedback metadata ───────────────────────────────────────
// Maps each regenerable copy content-key to a short, human-readable block label
// shown in the per-block regenerate control. A content-key absent here (pure
// image/status keys) is not offered as a regenerable copy block.
export const LP_COPY_BLOCK_LABELS: Record<string, string> = {
  ...Object.fromEntries(LP_SMB_HOME_BLOCK_KEYS.map(key => [key, key.slice(2).replace(/([a-z])([A-Z])/g, "$1 $2")])),
  hero: "Hero",
  leadin: "Intro line",
  showcase: "Showcase",
  productContext: "Product context",
  advisory: "Advisory",
  stations: "Problem stations",
  villain: "Villain",
  quotes: "Pull quotes",
  originCopy: "Origin story",
  timeline: "Timeline",
  membership: "Membership",
  proof: "Proof",
  alternatives: "Alternatives",
  philosophy: "Philosophy",
  differentiator: "Differentiator",
  faq: "FAQ",
  reviews: "Reviews",
  finalCta: "Final CTA",
  // Product Hero (PDP) template copy blocks. Each regenerates through the
  // SINGLE product-hero workflow, with the server merging back ONLY the
  // requested block's copy (same model as the advertorial's per-section regen).
  phBuyBox: "Buy box",
  phTrustBar: "Trust bar",
  phBenefits: "Benefits",
  phHowItWorks: "How it works",
  phReviews: "Reviews",
  phGuarantee: "Guarantee",
  phFaq: "FAQ",
  phFinalCta: "Closing CTA",
  phDisclaimer: "Disclaimer",
  phStickyCta: "Sticky CTA",
  // Problem / Solution (PAS) template copy blocks. Each regenerates through
  // the SINGLE problem-solution workflow, with the server merging back ONLY
  // the requested block's copy (same model as the product-hero's per-section
  // regen).
  psHero: "Hero",
  psProblem: "Problem",
  psWhyFail: "Why fixes fail",
  psMechanism: "Mechanism",
  psSolution: "Solution reveal",
  psProof: "Proof",
  psOffer: "Offer box",
  psGuarantee: "Guarantee",
  psFaq: "FAQ",
  psDisclaimer: "Disclaimer",
  psStickyCta: "Sticky CTA",
  // Offer Stack (value-stack close) template copy blocks. Each regenerates
  // through the SINGLE offer-stack workflow, with the server merging back
  // ONLY the requested block's copy (same model as the product-hero /
  // problem-solution per-section regen).
  osHero: "Hero",
  osIntro: "Product intro",
  osStack: "Value stack",
  osBonuses: "Bonuses",
  osScarcity: "Scarcity",
  osReviews: "Reviews",
  osGuarantee: "Guarantee",
  osFaq: "FAQ",
  osDisclaimer: "Disclaimer",
  osStickyCta: "Sticky CTA",
};

// Resolve a copy-block key to its control label. Static blocks come from
// LP_COPY_BLOCK_LABELS; the Advertorial's per-section regen keys are dynamic
// ("advertorial-section-<index>") so they resolve here instead of bloating the
// static map. Returns undefined for unknown keys (no control rendered).
export function lpCopyBlockLabel(blockKey: string): string | undefined {
  const adv = /^advertorial-section-(\d+)$/.exec(blockKey);
  if (adv) return `Article section ${Number(adv[1]) + 1}`;
  // Cold Drip Offer (Gaffle) per-section regen keys: "cold-drip-<sectionKey>".
  const cd = /^cold-drip-([A-Za-z]+)$/.exec(blockKey);
  if (cd) {
    // Lazy import would create a cycle risk; a tiny inline label map keeps this
    // module dependency-free. Fall back to the raw key for unknown sections.
    return `Cold Drip · ${cd[1].replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())}`;
  }
  // Lead Magnet (Gaffle) per-section regen keys: "coffee-shop-<sectionKey>"
  // (internal template key stays "coffee-shop"; only the display name changed).
  const cs = /^coffee-shop-([A-Za-z]+)$/.exec(blockKey);
  if (cs) {
    return `Lead Magnet · ${cs[1].replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())}`;
  }
  // Temecula Passport (Gaffle mystery-drop) per-section regen keys:
  // "temecula-passport-<sectionKey>".
  const tp = /^temecula-passport-([A-Za-z]+)$/.exec(blockKey);
  if (tp) {
    return `Temecula Passport · ${tp[1].replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())}`;
  }
  // PapaBear Label imported-template sections. Keep this closed-world so a
  // typo cannot surface a regenerate control whose server key will be rejected.
  const papabear = /^papabear-(hero|reasons|buyBox|reviews|faq|stickyBar)$/.exec(blockKey);
  if (papabear) {
    const labels: Record<string, string> = {
      hero: "Hero",
      reasons: "Five Reasons",
      buyBox: "Bundle & Purchase",
      reviews: "Reviews",
      faq: "FAQ",
      stickyBar: "Sticky Purchase Bar",
    };
    return `PapaBear · ${labels[papabear[1]]}`;
  }
  return LP_COPY_BLOCK_LABELS[blockKey];
}

// The section types that render AI-generated image slots AND support
// regenerating them, so the section Regenerate offers a "Text / Images / Both"
// scope. Kept as an explicit per-section-type set rather than derived from
// shared content keys: keys like `productContext` appear in sections that don't
// render their own images (e.g. Belief reuses only the copy), which would
// surface a dead "Images" option there. This list must stay in lockstep with
// the page's `triggerSectionImages` switch (the code that re-renders images).
export const LP_IMAGE_SECTION_TYPES = new Set<LpSectionType>([
  "hero",
  "productShowcase",
  "farmstand",
  "proof",
  "alternatives",
  "philosophy",
  "differentiator",
]);

// The ordered list of regenerable copy blocks a section carries, derived from
// the section registry's content-key mapping (filtered to known copy keys).
export function lpSectionCopyBlocks(
  type: LpSectionType,
): Array<{ key: string; label: string }> {
  const def = LP_SECTION_REGISTRY[type];
  if (!def) return [];
  return def.contentKeys
    .filter((k) => k in LP_COPY_BLOCK_LABELS)
    .map((k) => ({ key: k, label: LP_COPY_BLOCK_LABELS[k] }));
}

// True when a section renders AI image slots that can be regenerated (so the
// "Images" / "Both" scope is meaningful). See LP_IMAGE_SECTION_TYPES.
export function lpSectionHasImages(type: LpSectionType): boolean {
  return LP_IMAGE_SECTION_TYPES.has(type);
}

// Build the canonical, fully-ordered sections array from a nested
// generatedContent object. Every known block is included in canonical order;
// fields are derived from the live content (empty when the workflow hasn't
// produced that content yet — the block then renders its loading/fallback
// state exactly as before).
export function deriveLpSections(gc: unknown): LpSection[] {
  const content = asContent(gc);
  return lpTemplateSectionTypes(content).map((type) => ({
    id: type,
    type,
    fields: extractSectionFields(type, content),
  }));
}

// Template-scoped canonical section sets. Section-based templates other than
// the default long-form page list their canonical section types here; every
// other templateType (including the blob templates, which never reach the
// sections pipeline, and legacy pages with no templateType) falls back to the
// long-form set — keeping existing pages byte-identical.
export const LP_TEMPLATE_SECTION_TYPES: Record<string, readonly LpSectionType[]> = {
  "smb-home": LP_SMB_HOME_BLOCK_KEYS,
  "product-hero": LP_PRODUCT_HERO_SECTION_TYPES,
  "problem-solution": LP_PROBLEM_SOLUTION_SECTION_TYPES,
  "offer-stack": LP_OFFER_STACK_SECTION_TYPES,
};

// The canonical section types for a generatedContent object, scoped by its
// templateType so one template's sections never leak onto another's pages.
export function lpTemplateSectionTypes(gc: unknown): readonly LpSectionType[] {
  const content = asContent(gc);
  if ((content.studioDesign as { rendererTemplateKey?: string } | undefined)?.rendererTemplateKey === "smb-home")
    return LP_SMB_HOME_BLOCK_KEYS;
  const templateType = typeof content.templateType === "string" ? content.templateType : "";
  return LP_TEMPLATE_SECTION_TYPES[templateType] ?? LP_LONG_FORM_SECTION_TYPES;
}

// The render-time source of truth. Prefers a persisted `sections` array (its
// order + identity + membership are authoritative, supporting future
// reorder/remove) but ALWAYS re-derives each section's `fields` from the live
// nested content — never trusting any stored field snapshot, so the array can
// never go stale relative to per-section merge writes. Falls back to the
// canonical derivation for legacy pages (no `sections` key) or if the stored
// value is malformed.
export function getLpSections(gc: unknown): LpSection[] {
  const content = asContent(gc);
  const stored = content.sections;
  if (Array.isArray(stored) && stored.length > 0) {
    try {
      const seen = new Set<LpSectionType>();
      const ordered: LpSection[] = [];
      for (const entry of stored) {
        if (!entry || typeof entry !== "object") continue;
        const rawType = (entry as { type?: unknown }).type;
        // Expand a legacy composite (e.g. "remaining") into its ordered
        // sub-sections in place, so older persisted/published pages keep
        // rendering the full long-form page.
        const expansion =
          typeof rawType === "string" ? LEGACY_COMPOSITE_EXPANSIONS[rawType] : undefined;
        if (expansion) {
          for (const subType of expansion) {
            if (seen.has(subType)) continue;
            seen.add(subType);
            ordered.push({ id: subType, type: subType, fields: extractSectionFields(subType, content) });
          }
          continue;
        }
        if (!isLpSectionType(rawType) || seen.has(rawType)) continue;
        seen.add(rawType);
        const rawId = (entry as { id?: unknown }).id;
        const id = typeof rawId === "string" && rawId ? rawId : rawType;
        const hidden = Boolean((entry as { hidden?: unknown }).hidden);
        ordered.push({ id, type: rawType, fields: extractSectionFields(rawType, content), hidden });
      }
      if (ordered.length > 0) return ordered;
    } catch (err) {
      console.warn(
        "[lp-express-sections] malformed persisted sections; deriving from content",
        err,
      );
    }
  }
  return deriveLpSections(content);
}

// Stamp a generatedContent object with its ordered `sections` array for
// persistence. Idempotent: preserves an existing stored order (via
// getLpSections) and re-derives fields, so re-stamping after a per-section
// merge never reorders or drops sections.
export function withLpSections(gc: unknown): Record<string, unknown> {
  const content = { ...asContent(gc) };
  content.sections = getLpSections(content);
  return content;
}

// =============================================================================
// WYSIWYG styling model — global theme + per-element/section overrides.
//
// All styling is stored on generatedContent (never the brand): a single global
// `theme` object retunes the whole page, and a `styles` map keyed by the same
// stable element keys used for hide/delete (e.g. `f:hero.headline`) plus
// section keys (`sec:<sectionType>`) holds per-element/section overrides.
//
// CRITICAL: values are constrained to a safe allow-list — enum fonts, validated
// hex colors, enum spacing/shape/fill, clamped font sizes. We NEVER store raw
// arbitrary CSS, so applying these read-only on the public page can't inject
// styles a user didn't pick.
// =============================================================================

// Curated, web-safe font stacks. Only these keys may be stored; the resolved
// CSS font-family stack lives here (never user-supplied).
export const LP_THEME_FONTS: Record<string, string> = {
  inter: '"Inter", ui-sans-serif, system-ui, -apple-system, sans-serif',
  system: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  poppins: '"Poppins", "Inter", ui-sans-serif, system-ui, sans-serif',
  montserrat: '"Montserrat", "Inter", ui-sans-serif, system-ui, sans-serif',
  georgia: 'Georgia, Cambria, "Times New Roman", serif',
  playfair: '"Playfair Display", Georgia, "Times New Roman", serif',
  merriweather: '"Merriweather", Georgia, "Times New Roman", serif',
  mono: 'ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, monospace',
};
export const LP_FONT_KEYS = Object.keys(LP_THEME_FONTS);
export type LpFontKey = string;

// ── Per-block font override resolution ───────────────────────────────────────
// A text block's `fontFamily` override is stored as ONE of exactly three safe
// shapes (anything else is dropped by the sanitizer and never persisted):
//   1. a curated theme key (a key of LP_THEME_FONTS), e.g. "poppins"
//   2. a Google Fonts catalog family name (GOOGLE_FONT_CATALOG), e.g. "Lato"
//   3. an opaque brand-font token "brand:<savedFontId>" that only ever resolves
//      to one of the brand's OWN saved fonts.
const GOOGLE_FONT_FAMILY_SET = new Set(GOOGLE_FONT_CATALOG.map((f) => f.family));
const GOOGLE_FONT_CATEGORY_BY_FAMILY: Record<string, GoogleFontCategory> =
  Object.fromEntries(GOOGLE_FONT_CATALOG.map((f) => [f.family, f.category]));

// The brand-font token shape. The id segment allows the characters produced by
// crypto.randomUUID() and the Date/random fallback id (hex, digits, hyphens,
// underscores, alphanumerics).
export const LP_BRAND_FONT_TOKEN_RE = /^brand:[A-Za-z0-9_-]+$/;

export function lpBrandFontToken(id: string): string {
  return `brand:${id}`;
}

function lpBrandFontTokenId(token: string): string | null {
  return LP_BRAND_FONT_TOKEN_RE.test(token)
    ? token.slice("brand:".length)
    : null;
}

// Validate a stored fontFamily override to one of the three safe shapes above.
// Brand tokens are validated by SHAPE only here (the server can't know a brand's
// saved-font ids); resolution to a real family happens at render, and a token
// that no longer maps to a usable brand font simply yields no override.
export function sanitizeLpFontFamily(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  if (!t) return undefined;
  if (t in LP_THEME_FONTS) return t;
  if (GOOGLE_FONT_FAMILY_SET.has(t)) return t;
  if (LP_BRAND_FONT_TOKEN_RE.test(t)) return t;
  return undefined;
}

// A generic fallback tail for a Google family, chosen from its catalog category
// so text stays legible while the web font loads.
function googleFontFallback(family: string): string {
  switch (GOOGLE_FONT_CATEGORY_BY_FAMILY[family]) {
    case "serif":
      return BRAND_FONT_SERIF_FALLBACK;
    case "monospace":
      return 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';
    case "handwriting":
      return "cursive";
    case "display":
    case "sans-serif":
    default:
      return BRAND_FONT_SANS_FALLBACK;
  }
}

// Resolve a stored fontFamily override to a concrete CSS font-family stack, or
// undefined when there's no (usable) override. `brandFonts` is the brand's saved
// font list, needed only to resolve "brand:<id>" tokens.
export function resolveLpElementFontStack(
  fontFamily: string | null | undefined,
  brandFonts?: BrandFont[] | null,
): string | undefined {
  if (!fontFamily || typeof fontFamily !== "string") return undefined;
  const t = fontFamily.trim();
  if (!t) return undefined;
  if (t in LP_THEME_FONTS) return LP_THEME_FONTS[t];
  const brandId = lpBrandFontTokenId(t);
  if (brandId) {
    const font = (brandFonts ?? []).find((f) => f.id === brandId);
    return font && isUsableBrandFont(font) ? brandFontCssStack(font) : undefined;
  }
  if (GOOGLE_FONT_FAMILY_SET.has(t)) return `"${t}", ${googleFontFallback(t)}`;
  return undefined;
}

// Collect the fonts referenced by a page's per-block style overrides that must
// be LOADED (curated keys resolve to globally-preloaded families, so skip them):
//   • Google catalog families referenced directly OR via a brand "google" font
//   • uploaded brand fonts referenced via a brand token
export function collectLpElementFontLoads(
  styles: Record<string, LpElementStyle> | null | undefined,
  brandFonts?: BrandFont[] | null,
  // The page theme — its ctaFontFamily uses the same grammar as per-block
  // overrides and must be loaded the same way (preview + published page).
  theme?: LpTheme | null,
): { googleFamilies: string[]; uploadedFonts: BrandFont[] } {
  const googleFamilies: string[] = [];
  const uploadedFonts: BrandFont[] = [];
  const seenGoogle = new Set<string>();
  const seenUpload = new Set<string>();
  const tokens: unknown[] = Object.values(styles ?? {}).map(
    (style) => style?.fontFamily,
  );
  if (theme?.ctaFontFamily) tokens.push(theme.ctaFontFamily);
  for (const ff of tokens) {
    if (!ff || typeof ff !== "string") continue;
    if (ff in LP_THEME_FONTS) continue; // curated → globally preloaded
    const brandId = lpBrandFontTokenId(ff);
    if (brandId) {
      const font = (brandFonts ?? []).find((f) => f.id === brandId);
      if (!font || !isUsableBrandFont(font)) continue;
      if (font.source === "google") {
        if (!seenGoogle.has(font.family)) {
          seenGoogle.add(font.family);
          googleFamilies.push(font.family);
        }
      } else if (font.url && !seenUpload.has(font.id)) {
        seenUpload.add(font.id);
        uploadedFonts.push(font);
      }
      continue;
    }
    if (GOOGLE_FONT_FAMILY_SET.has(ff) && !seenGoogle.has(ff)) {
      seenGoogle.add(ff);
      googleFamilies.push(ff);
    }
  }
  return { googleFamilies, uploadedFonts };
}

export const LP_BUTTON_SHAPES = ["rounded", "pill", "square"] as const;
export type LpButtonShape = (typeof LP_BUTTON_SHAPES)[number];
export const LP_BUTTON_FILLS = ["solid", "outline"] as const;
export type LpButtonFill = (typeof LP_BUTTON_FILLS)[number];
export const LP_SPACING_SCALES = ["compact", "default", "spacious"] as const;
export type LpSpacing = (typeof LP_SPACING_SCALES)[number];

export const LP_TEXT_ALIGNS = ["left", "center", "right"] as const;
export type LpTextAlign = (typeof LP_TEXT_ALIGNS)[number];
export const LP_FONT_WEIGHTS = ["normal", "bold"] as const;
export type LpFontWeight = (typeof LP_FONT_WEIGHTS)[number];

// The whole-page theme. Every field is optional — an absent field means "use the
// AI-generated default" so resetting the theme is just clearing the object.
export interface LpTheme {
  headingFont?: LpFontKey;
  bodyFont?: LpFontKey;
  primaryColor?: string; // hex — overrides --color-brand-navy
  accentColor?: string; // hex — overrides --color-brand-coral
  buttonShape?: LpButtonShape;
  buttonFill?: LpButtonFill;
  spacing?: LpSpacing;
  // Explicit CTA button styling. Seeded ONCE at page generation from the
  // brand's Design System CTA style (see brandCtaToLpThemeSeed) and persisted
  // on the page, so later brand edits never retroactively restyle existing or
  // published pages. Per-page theme edits win because this IS the page theme.
  ctaColor?: string; // hex — CTA button fill
  ctaTextColor?: string; // hex — CTA button text; unset ⇒ luminance auto-contrast
  // Uppercase every CTA button label on the page.
  ctaAllCaps?: boolean;
  // CTA button label font — same grammar as LpElementStyle.fontFamily (curated
  // key, Google catalog family, or "brand:<id>" token). Unset ⇒ each button
  // keeps whatever font its section already uses.
  ctaFontFamily?: string;
}

// A per-element / per-section style override. Text fields apply to headings /
// text blocks; background/paddingY apply to section-level blocks.
export interface LpElementStyle {
  fontSize?: number; // px, clamped 10–96
  fontWeight?: LpFontWeight;
  align?: LpTextAlign;
  color?: string; // hex
  // Per-block font override — a curated key, a Google catalog family, or a
  // "brand:<id>" token (see sanitizeLpFontFamily / resolveLpElementFontStack).
  fontFamily?: string;
  background?: string; // hex (section-level tone)
  paddingY?: LpSpacing; // section-level vertical padding
}

const LP_HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Validate a color string to a 3/6-digit hex (lowercased). Returns undefined for
// anything else — we never store arbitrary CSS color expressions.
export function sanitizeLpColor(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = v.trim().toLowerCase();
  return LP_HEX_RE.test(t) ? t : undefined;
}

function pickEnum<T extends string>(v: unknown, allowed: readonly T[]): T | undefined {
  return typeof v === "string" && (allowed as readonly string[]).includes(v)
    ? (v as T)
    : undefined;
}

function pickFont(v: unknown): LpFontKey | undefined {
  return typeof v === "string" && v in LP_THEME_FONTS ? v : undefined;
}

// Sanitize a (possibly partial) theme object, dropping any unknown/invalid
// values. Safe to call with untrusted input on the server.
export function sanitizeLpTheme(v: unknown): LpTheme {
  const src = v && typeof v === "object" ? (v as Record<string, unknown>) : {};
  const out: LpTheme = {};
  const headingFont = pickFont(src.headingFont);
  if (headingFont) out.headingFont = headingFont;
  const bodyFont = pickFont(src.bodyFont);
  if (bodyFont) out.bodyFont = bodyFont;
  const primaryColor = sanitizeLpColor(src.primaryColor);
  if (primaryColor) out.primaryColor = primaryColor;
  const accentColor = sanitizeLpColor(src.accentColor);
  if (accentColor) out.accentColor = accentColor;
  const buttonShape = pickEnum(src.buttonShape, LP_BUTTON_SHAPES);
  if (buttonShape) out.buttonShape = buttonShape;
  const buttonFill = pickEnum(src.buttonFill, LP_BUTTON_FILLS);
  if (buttonFill) out.buttonFill = buttonFill;
  const spacing = pickEnum(src.spacing, LP_SPACING_SCALES);
  if (spacing) out.spacing = spacing;
  const ctaColor = sanitizeLpColor(src.ctaColor);
  if (ctaColor) out.ctaColor = ctaColor;
  const ctaTextColor = sanitizeLpColor(src.ctaTextColor);
  if (ctaTextColor) out.ctaTextColor = ctaTextColor;
  if (src.ctaAllCaps === true) out.ctaAllCaps = true;
  const ctaFontFamily = sanitizeLpFontFamily(src.ctaFontFamily);
  if (ctaFontFamily) out.ctaFontFamily = ctaFontFamily;
  return out;
}

// Sanitize a (possibly partial) per-element/section style, dropping invalid
// values and clamping font size to a sane range.
export function sanitizeLpElementStyle(v: unknown): LpElementStyle {
  const src = v && typeof v === "object" ? (v as Record<string, unknown>) : {};
  const out: LpElementStyle = {};
  if (typeof src.fontSize === "number" && Number.isFinite(src.fontSize)) {
    out.fontSize = Math.min(96, Math.max(10, Math.round(src.fontSize)));
  }
  const fontWeight = pickEnum(src.fontWeight, LP_FONT_WEIGHTS);
  if (fontWeight) out.fontWeight = fontWeight;
  const align = pickEnum(src.align, LP_TEXT_ALIGNS);
  if (align) out.align = align;
  const color = sanitizeLpColor(src.color);
  if (color) out.color = color;
  const fontFamily = sanitizeLpFontFamily(src.fontFamily);
  if (fontFamily) out.fontFamily = fontFamily;
  const background = sanitizeLpColor(src.background);
  if (background) out.background = background;
  const paddingY = pickEnum(src.paddingY, LP_SPACING_SCALES);
  if (paddingY) out.paddingY = paddingY;
  return out;
}

// Merge a sanitized style patch over an existing style. A patch field explicitly
// set to null/"" clears that field (used by the toolbar's "clear" affordances);
// undefined leaves it untouched.
export function mergeLpElementStyle(
  existing: LpElementStyle | undefined,
  patch: Record<string, unknown>,
): LpElementStyle {
  const next: LpElementStyle = { ...(existing || {}) };
  const clearable: (keyof LpElementStyle)[] = [
    "fontSize",
    "fontWeight",
    "align",
    "color",
    "fontFamily",
    "background",
    "paddingY",
  ];
  for (const k of clearable) {
    if (!(k in patch)) continue;
    const raw = patch[k];
    if (raw == null || raw === "") {
      delete next[k];
    }
  }
  const sanitized = sanitizeLpElementStyle(patch);
  return { ...next, ...sanitized };
}

// =============================================================================
// Component reorder model — per-page ordering of stacked child components within
// a single container (NOT cross-section reordering; sections own that).
//
// Stored on generatedContent.componentOrder as a map of stable group key →
// ordered list of element keys (the same stable keys used for hide/delete, e.g.
// "hero:kit-card"). An absent group means "use the canonical author order".
// =============================================================================
export type LpComponentOrder = Record<string, string[]>;

// Sanitize a (possibly partial) component-order map: keep only non-empty string
// group keys mapping to deduped non-empty string element keys. Safe to call with
// untrusted input on the server. Drops empty groups.
export function sanitizeLpComponentOrder(v: unknown): LpComponentOrder {
  if (!v || typeof v !== "object") return {};
  const out: LpComponentOrder = {};
  for (const [rawGroup, list] of Object.entries(v as Record<string, unknown>)) {
    const group = typeof rawGroup === "string" ? rawGroup.trim() : "";
    if (!group || !Array.isArray(list)) continue;
    const seen = new Set<string>();
    const keys: string[] = [];
    for (const k of list) {
      if (typeof k !== "string") continue;
      const t = k.trim();
      if (!t || seen.has(t)) continue;
      seen.add(t);
      keys.push(t);
    }
    if (keys.length > 0) out[group] = keys;
  }
  return out;
}

// ── Per-page media overrides ──
// A map of stable slot key (e.g. "hero:0", "alternativesImages:brand-hero")
// → a user-uploaded durable media URL that SHADOWS the AI-generated slot at
// render time. Stored on generatedContent.mediaOverrides (never the brand) so
// the underlying AI slot + its regeneration context are preserved untouched.
export type LpMediaOverride = { url: string };
export type LpMediaOverrides = Record<string, LpMediaOverride>;

// Sanitize a (possibly partial) media-override map: keep only non-empty string
// slot keys mapping to objects with a non-empty string `url`. Safe to call with
// untrusted input on the server. Drops empty/invalid entries.
export function sanitizeLpMediaOverrides(v: unknown): LpMediaOverrides {
  if (!v || typeof v !== "object") return {};
  const out: LpMediaOverrides = {};
  for (const [rawKey, val] of Object.entries(v as Record<string, unknown>)) {
    const key = typeof rawKey === "string" ? rawKey.trim() : "";
    if (!key) continue;
    const url = val && typeof val === "object" ? (val as any).url : undefined;
    if (typeof url === "string" && url.trim()) out[key] = { url: url.trim() };
  }
  return out;
}

// ── Per-page review overrides ──
// A map of stable slot key (e.g. "hero") → a SNAPSHOT of the real product
// review the user picked to feature there. Stored on
// generatedContent.reviewOverrides (never the brand). We snapshot the review's
// fields (rather than a review id) so the selection is durable — it survives a
// publish snapshot and any later change/removal of the source review, mirroring
// how mediaOverrides stores the chosen URL directly.
export type LpReviewOverride = { reviewer: string | null; reviewText: string; rating: number };
export type LpReviewOverrides = Record<string, LpReviewOverride>;

// Sanitize a (possibly partial) review-override map: keep only non-empty string
// slot keys mapping to objects with a non-empty string `reviewText`. `reviewer`
// is trimmed to a string or null; `rating` is clamped to an integer 1–5
// (defaults to 5). Safe to call with untrusted input on the server.
export function sanitizeLpReviewOverrides(v: unknown): LpReviewOverrides {
  if (!v || typeof v !== "object") return {};
  const out: LpReviewOverrides = {};
  for (const [rawKey, val] of Object.entries(v as Record<string, unknown>)) {
    const key = typeof rawKey === "string" ? rawKey.trim() : "";
    if (!key || !val || typeof val !== "object") continue;
    const reviewText =
      typeof (val as any).reviewText === "string" ? (val as any).reviewText.trim() : "";
    if (!reviewText) continue;
    const rawReviewer = (val as any).reviewer;
    const reviewer =
      typeof rawReviewer === "string" && rawReviewer.trim() ? rawReviewer.trim() : null;
    const rawRating = (val as any).rating;
    const rating =
      typeof rawRating === "number" && rawRating > 0
        ? Math.min(5, Math.max(1, Math.round(rawRating)))
        : 5;
    out[key] = { reviewer, reviewText, rating };
  }
  return out;
}

// Merge a sanitized theme patch over an existing theme; null/"" clears a field.
export function mergeLpTheme(
  existing: LpTheme | undefined,
  patch: Record<string, unknown>,
): LpTheme {
  const next: LpTheme = { ...(existing || {}) };
  const clearable: (keyof LpTheme)[] = [
    "headingFont",
    "bodyFont",
    "primaryColor",
    "accentColor",
    "buttonShape",
    "buttonFill",
    "spacing",
    "ctaColor",
    "ctaTextColor",
    "ctaAllCaps",
    "ctaFontFamily",
  ];
  for (const k of clearable) {
    if (!(k in patch)) continue;
    const raw = patch[k];
    // `false` clears boolean theme flags (the sanitizer only ever stores true).
    if (raw == null || raw === "" || raw === false) delete next[k];
  }
  const sanitized = sanitizeLpTheme(patch);
  return { ...next, ...sanitized };
}
